// CORNER_V2: Post-send "Save this for next time?" prompt.
//
// Shown inside the wizard's terminal-success screen. Has two modes:
//
//   mode='workflow'  — first-time send, no saved workflow exists yet.
//                      Clicking save creates a UserWorkflow + first
//                      template in one shot via saveContractSendAsWorkflow.
//
//   mode='template'  — workflow already exists; user just sent a new doc
//                      through it. Clicking save adds this doc as a new
//                      template under the existing workflow.
//
// Both modes call the same callback shape (defined via props) so the
// parent owns the actual API logic and can stub it during UI-first
// development.
import { useEffect, useState, type CSSProperties } from 'react';
import { APP } from '../tokens';

export type SaveAsWorkflowMode = 'workflow' | 'template';

export interface SaveAsWorkflowPromptProps {
  mode: SaveAsWorkflowMode;
  /** Default name in the input. Pre-fill from doc filename or workflow name. */
  defaultName: string;
  /** Workflow name shown in copy when mode='template'. */
  workflowName?: string;
  /** Called when user confirms. Parent handles the actual API call. */
  onSave: (name: string) => void | Promise<void>;
  /** Called when user dismisses. Parent should remember "user said no" so
   *  we don't re-prompt on every render. */
  onDismiss: () => void;
}

export function SaveAsWorkflowPrompt({
  mode,
  defaultName,
  workflowName,
  onSave,
  onDismiss,
}: SaveAsWorkflowPromptProps) {
  const [name, setName] = useState(defaultName);
  const [saving, setSaving] = useState(false);

  // Reset name field if defaultName changes (e.g., user opens a different
  // run before this prompt was dismissed).
  useEffect(() => { setName(defaultName); }, [defaultName]);

  const canSave = name.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(name.trim());
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'workflow'
    ? 'Save this for next time?'
    : 'Save as a template?';
  const blurb = mode === 'workflow'
    ? 'We\'ll remember the recipient setup, the field positions, and the document layout. Next time you can send a similar contract in seconds — no chat required.'
    : `Add this document as a template under "${workflowName ?? 'this workflow'}". Future sends with the same layout will skip detection and use these field positions automatically.`;
  const inputLabel = mode === 'workflow' ? 'Workflow name' : 'Template name';
  const saveLabel = mode === 'workflow' ? 'Save workflow' : 'Save template';

  return (
    <div style={CARD}>
      <div style={SPARKLE_HEADER}>
        <span style={SPARKLE}>✨</span>
        <span style={TITLE}>{title}</span>
      </div>
      <p style={BLURB}>{blurb}</p>

      <label style={INPUT_LABEL}>{inputLabel}</label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={mode === 'workflow' ? 'e.g. Acme NDA flow' : 'e.g. Acme NDA — Jan 2026'}
        disabled={saving}
        style={INPUT}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canSave) handleSave();
          if (e.key === 'Escape') onDismiss();
        }}
      />

      <div style={ACTIONS}>
        <button
          type="button"
          onClick={onDismiss}
          disabled={saving}
          style={SECONDARY_BTN}
        >
          Not now
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          style={{
            ...PRIMARY_BTN,
            opacity: canSave ? 1 : 0.5,
            cursor: canSave ? 'pointer' : 'not-allowed',
          }}
        >
          {saving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const CARD: CSSProperties = {
  marginTop: 16,
  padding: '18px 20px',
  borderRadius: 14,
  border: `1px solid ${APP.border}`,
  backgroundColor: 'var(--app-bg-card-body)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  fontFamily: APP.fontFamily,
  // Subtle accent so the prompt reads as a *gift* (the user just saved
  // future-them work) rather than another todo.
  boxShadow: '0 4px 16px rgba(217, 118, 69, 0.08)',
};

const SPARKLE_HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const SPARKLE: CSSProperties = {
  fontSize: 18,
};

const TITLE: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: APP.textPrimary,
};

const BLURB: CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.5,
  color: APP.textMuted,
};

const INPUT_LABEL: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginTop: 4,
};

const INPUT: CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  backgroundColor: 'var(--app-bg-input)',
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
  outline: 'none',
};

const ACTIONS: CSSProperties = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  marginTop: 8,
};

const PRIMARY_BTN: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: APP.accentWorkflowPrimary,
  color: '#FFFFFF',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: APP.fontFamily,
};

const SECONDARY_BTN: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  cursor: 'pointer',
  backgroundColor: 'transparent',
  color: APP.textPrimary,
  fontSize: 13,
  fontWeight: 500,
  fontFamily: APP.fontFamily,
};
